import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import { RepoCache } from "@repo/shared-client/repoCache";
import {
  CreateAccountDtoSchema,
  PatchAccountDtoSchema,
  type Account,
  type CreateAccountDto,
} from "@repo/types";
import { cacheDefaults } from "./common";
import { apiService } from "@/services/apiService";
import type { Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import type { EmptyObject } from "@repo/lib/types";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type AccountGetSelfKey = { type: "getSelf" };
type AccountGetByEmailKey = { type: "getByEmail"; email: string };
type AccountKey = AccountGetSelfKey | AccountGetByEmailKey;

export type AccountIdPath = EmptyObject;

export class AccountRepository {
  private cache: RepoCache<string, Account, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<AccountIdPath, Account>();
  private myEmail: string | null = null;

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as AccountKey;

        switch (key.type) {
          case "getSelf": {
            const account = (await apiService.v1.getMyAccount()).data;
            if (account) {
              this.myEmail = account.email;
              publish(account.email, account);
            }
            break;
          }
          case "getByEmail": {
            const { email } = key;
            const account = (await apiService.v1.getAccountByEmail({ email }))
              .data;
            publish(email, account);
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as AccountKey;

        switch (key.type) {
          case "getSelf": {
            return this.myEmail ? table.get(this.myEmail) ?? null : null;
          }
          case "getByEmail": {
            return table.get(key.email) ?? null;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { email } = entryData;
        // affect
        affectsKey(this.getByEmailKey(email));
        if (email === this.myEmail) {
          affectsKey(this.getSelfKey());
        }
      },
    });

    baseRepo.addRepo(this);
  }

  private getSelfKey(): AccountGetSelfKey {
    return { type: "getSelf" };
  }
  private getByEmailKey(email: string): AccountGetByEmailKey {
    return { type: "getByEmail", email };
  }

  getMyAccount(forceReload: boolean = false): Observable<Account | null> {
    const key = this.getSelfKey();
    return this.cache.observable(key, { forceReload });
  }
  getMyAccountCached(): Account | null {
    const key = this.getSelfKey();
    return this.cache.collectIfUnexpired(key, null);
  }

  getByEmail(
    email: string,
    forceReload: boolean = false
  ): Observable<Account | null> {
    if (!email) {
      return nullObservable;
    }
    const key = this.getByEmailKey(email);
    return this.cache.observable(key, { forceReload });
  }
  getByEmailCached(email: string): Account | null {
    const key = this.getByEmailKey(email);
    return this.cache.collectIfUnexpired(key, null);
  }

  async createSelf(data: CreateAccountDto): Promise<Account | null> {
    if (!data.firstName || !data.lastName) {
      throw new Error("First / last name missing from account data");
    }

    const hooks = this.hooks.execute({}, "create");
    data = (await hooks.before(data)) as CreateAccountDto;

    let created = (
      await apiService.v1.createMyAccount(CreateAccountDtoSchema.parse(data))
    ).data;

    // publish new account
    if (created) {
      created = await hooks.after(created);
      this.cache.publish(created.email, created);
    }

    return created;
  }

  async updateSelf(data: Partial<Account>): Promise<Account | null> {
    const hooks = this.hooks.execute({}, "update");
    data = await hooks.before(data);

    let updated = (
      await apiService.v1.updateMyAccount(PatchAccountDtoSchema.parse(data))
    ).data;

    // publish updated account
    if (updated) {
      updated = await hooks.after(updated);
      this.cache.publish(updated.email, updated);
    }

    return updated;
  }

  invalidate(email: string) {
    this.cache.invalidateEntry(email);
  }

  invalidateMyAccount() {
    if (this.myEmail) this.cache.invalidateEntry(this.myEmail);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<Account>): Account {
    return baseRepo.init({
      name: "",
      email: "",
      emailVerified: null,
      image: "",
      lastLoginTime: new Date(),
      firstName: "",
      lastName: "",
      ...data,
    });
  }
}

export const accountRepo = new AccountRepository();
